import { useState, useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'

import {
  faCircleCheck,
  faCopy,
  faPlus,
  faTrash,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { cn } from '@/utils'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSetAtom } from 'jotai'

import { ENVIRONMENT } from '@/shared/environment'

import { ApiTokenScope, Features, type Team } from '~shared/types'

import { useRundownsByTeam } from '@/entities/rundown/api/hooks'

import {
  useDashboardPermission,
  DashboardPermissions,
} from '../../entities/user'
import {
  listApiTokens,
  mintApiToken,
  revokeApiToken,
  getApiTokenValue,
} from '../../firestore.js'
import { toastAtom } from '../../store/toast.store'
import { Button } from '../button'
import { Input } from '../input'
import AdvancedSelect from '../interactives/AdvancedSelect.jsx'

import DashboardWrapper from './DashboardWrapper'

import type { User } from 'firebase/auth'

type RoleChoice = 'owner' | 'showcaller' | 'editor' | 'viewer'
type ScopeKind = 'team' | 'rundown'
type TokenStatus = 'active' | 'revoked'
type ExpiryChoice = '24h' | '7d' | '30d' | 'never'

interface ApiTokenListItem {
  jti: string
  name: string
  createdBy: string
  createdAt: string
  lastUsedAt: string | null
  expiresAt: string | null
  status: TokenStatus
  scope: ApiTokenScope
  rundownId: string | null
}

interface MintedToken {
  jti: string
  token: string
  name: string
  scope: ApiTokenScope
  rundownId: string | null
  expiresAt: string | null
}

interface MintFormState {
  name: string
  kind: ScopeKind
  rundownId: string
  role: RoleChoice
  expiry: ExpiryChoice
}

/**
 * Compose the form's (kind, role) selection into a single ApiTokenScope code.
 * OWNER is only valid for team-scope; rundown_owner doesn't exist.
 */
function composeScope(kind: ScopeKind, role: RoleChoice): ApiTokenScope {
  if (kind === 'team') {
    if (role === 'owner') return ApiTokenScope.TEAM_OWNER
    if (role === 'showcaller') return ApiTokenScope.TEAM_SHOWCALLER
    if (role === 'editor') return ApiTokenScope.TEAM_EDITOR
    return ApiTokenScope.TEAM_VIEWER
  }
  if (role === 'showcaller') return ApiTokenScope.RUNDOWN_SHOWCALLER
  if (role === 'editor') return ApiTokenScope.RUNDOWN_EDITOR
  return ApiTokenScope.RUNDOWN_VIEWER
}

function decomposeScope(scope: ApiTokenScope): {
  kind: ScopeKind
  role: RoleChoice
} {
  switch (scope) {
    case ApiTokenScope.TEAM_OWNER:
      return { kind: 'team', role: 'owner' }
    case ApiTokenScope.TEAM_SHOWCALLER:
      return { kind: 'team', role: 'showcaller' }
    case ApiTokenScope.TEAM_EDITOR:
      return { kind: 'team', role: 'editor' }
    case ApiTokenScope.TEAM_VIEWER:
      return { kind: 'team', role: 'viewer' }
    case ApiTokenScope.RUNDOWN_SHOWCALLER:
      return { kind: 'rundown', role: 'showcaller' }
    case ApiTokenScope.RUNDOWN_EDITOR:
      return { kind: 'rundown', role: 'editor' }
    case ApiTokenScope.RUNDOWN_VIEWER:
      return { kind: 'rundown', role: 'viewer' }
    // Gracefully degrade on any unrecognized scope (e.g. a token minted under
    // an older code) rather than crashing the whole list.
    default:
      return { kind: 'team', role: 'viewer' }
  }
}

function roleLabel(role: RoleChoice): string {
  return role === 'owner'
    ? 'Owner'
    : role === 'showcaller'
      ? 'Showcall'
      : role === 'editor'
        ? 'Edit'
        : 'View'
}

function kindLabel(kind: ScopeKind): string {
  return kind === 'team' ? 'Team' : 'Rundown'
}

function defaultTokenName(): string {
  const now = new Date()
  const yyyy = now.getFullYear()
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  return `API token — ${yyyy}-${mm}-${dd}`
}

function buildEmptyForm(): MintFormState {
  return {
    name: defaultTokenName(),
    kind: 'team',
    rundownId: '',
    role: 'editor',
    expiry: '7d',
  }
}

function expiryToIso(choice: ExpiryChoice): string | null {
  if (choice === 'never') return null
  const ms =
    choice === '24h'
      ? 24 * 60 * 60 * 1000
      : choice === '7d'
        ? 7 * 24 * 60 * 60 * 1000
        : 30 * 24 * 60 * 60 * 1000
  return new Date(Date.now() + ms).toISOString()
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString()
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString()
}

export default function API() {
  const { team, plan } = useOutletContext<{
    team: Team
    user: User
    // TODO: align with project's plan typing once available
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    plan: any
  }>()
  const addToast = useSetAtom(toastAtom)

  const [showCreate, setShowCreate] = useState(false)
  const [revokingJti, setRevokingJti] = useState<string | null>(null)
  const [copyingJti, setCopyingJti] = useState<string | null>(null)
  const [form, setForm] = useState<MintFormState>(buildEmptyForm)
  const [justMinted, setJustMinted] = useState<MintedToken | null>(null)
  const [revealMinted, setRevealMinted] = useState(false)

  const isTeamAdmin = useDashboardPermission({
    permissions: DashboardPermissions.TEAM_ADMIN,
  })

  const _hasApi = !!plan?.features?.includes(Features.API)

  // Revoked tokens persist in Firestore (the token doc's `status: 'revoked'`
  // is the auth source of truth, so it must outlive the revoke action) but the
  // user view filters them out — "Revoke" already reads as "delete" in the UI.
  // Filter client-side; the list endpoint returns the full set and there are
  // only a few rows.
  const tokensQuery = useQuery({
    queryKey: ['api-tokens', team.id],
    queryFn: async (): Promise<ApiTokenListItem[]> => {
      const { data } = await listApiTokens(team.id)
      const all: ApiTokenListItem[] = data.tokens || []
      return all.filter((t) => t.status === 'active')
    },
    enabled: isTeamAdmin,
  })
  const tokens = tokensQuery.data ?? []
  const tokensLoading = tokensQuery.isLoading

  // Team rundowns power both the mint form's picker and the list's scope cell
  // (which shows the rundown name, not the opaque id). Fetch whenever the form
  // needs it or any listed token is rundown-scoped.
  const hasRundownScopedToken = tokens.some((t) => t.rundownId)
  const { data: rundowns } = useRundownsByTeam({
    params: { teamId: team.id },
    options: {
      enabled: (showCreate && form.kind === 'rundown') || hasRundownScopedToken,
    },
    hideToast: true,
  })

  const rundownOptions = useMemo(
    () =>
      (rundowns ?? []).map((r) => ({
        label: r.name || '(untitled rundown)',
        value: r.id,
      })),
    [rundowns],
  )

  const rundownNameById = useMemo(
    () =>
      new Map(
        (rundowns ?? []).map((r) => [r.id, r.name || '(untitled rundown)']),
      ),
    [rundowns],
  )

  const queryClient = useQueryClient()
  const invalidateTokens = () =>
    queryClient.invalidateQueries({ queryKey: ['api-tokens', team.id] })

  const mintMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { data } = await mintApiToken(team.id, payload)
      return data as MintedToken
    },
    onSuccess: (data) => {
      setJustMinted(data)
      setRevealMinted(false)
      setShowCreate(false)
      setForm(buildEmptyForm())
      void invalidateTokens()
    },
  })

  const revokeMutation = useMutation({
    mutationFn: async (jti: string) => {
      await revokeApiToken(team.id, jti)
    },
    onSuccess: () => {
      addToast({ title: 'Token revoked', type: 'success' })
      void invalidateTokens()
    },
    onSettled: () => setRevokingJti(null),
  })

  const handleCreateOpen = () => {
    setForm(buildEmptyForm())
    setShowCreate(true)
  }

  const handleMint = () => {
    if (!form.name.trim()) {
      addToast({ title: 'Name is required', type: 'fail' })
      return
    }
    if (form.kind === 'rundown' && !form.rundownId) {
      addToast({
        title: 'Pick a rundown for rundown-scope tokens',
        type: 'fail',
      })
      return
    }
    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      scope: composeScope(form.kind, form.role),
    }
    if (form.kind === 'rundown') payload.rundownId = form.rundownId
    const expiresAt = expiryToIso(form.expiry)
    if (expiresAt) payload.expiresAt = expiresAt
    mintMutation.mutate(payload)
  }

  const handleRevoke = (jti: string) => {
    if (!isTeamAdmin) return
    if (
      !window.confirm(
        'Revoke this token? Existing scripts using it will start failing immediately.',
      )
    )
      return
    setRevokingJti(jti)
    revokeMutation.mutate(jti)
  }

  const handleCopyMinted = async () => {
    if (!justMinted) return
    try {
      await navigator.clipboard.writeText(justMinted.token)
      addToast({
        title: 'Token copied to clipboard',
        type: 'success',
        duration: 3000,
      })
    } catch {
      // Clipboard can reject on focus/permission loss.
      addToast({ title: 'Could not copy to clipboard', type: 'fail' })
    }
  }

  // Re-reveal: fetch the stored JWT for an existing row and copy it. `token` is
  // null for tokens minted before value storage existed (and we won't re-sign,
  // since that could resurrect a bulk-revoked token) — guide the user to re-mint.
  const handleCopyToken = async (jti: string) => {
    if (!isTeamAdmin) return
    setCopyingJti(jti)
    try {
      let token: string | null
      try {
        const { data } = await getApiTokenValue(team.id, jti)
        token = data.token
      } catch {
        // The fetch failure already toasted via firestore.js's _errorHandler —
        // a second toast here would double up.
        return
      }
      if (!token) {
        addToast({
          title: 'Token value not available',
          body: 'This token predates value storage. Revoke it and mint a new one to get a copyable value.',
          type: 'fail',
          duration: 6000,
        })
        return
      }
      try {
        await navigator.clipboard.writeText(token)
        addToast({
          title: 'Token copied to clipboard',
          type: 'success',
          duration: 3000,
        })
      } catch {
        addToast({ title: 'Could not copy to clipboard', type: 'fail' })
      }
    } finally {
      setCopyingJti(null)
    }
  }

  return (
    <DashboardWrapper title="API">
      <p className="text-gray-300 text-sm">
        Control Rundown Studio via our API. Read about how this works{' '}
        <a
          className="underline"
          href={`${ENVIRONMENT.marketingLinks.docs}/api-v1/getting-started/`}
          target="_blank"
          rel="noreferrer"
        >
          in our docs
        </a>
        .
      </p>

      {/* Newly-minted token: surfaced right after a successful mint as a
          convenient first copy. The value is no longer one-shot — it can be
          re-copied any time from the list below (the per-row Copy button). */}
      {justMinted && (
        <div className="mt-6 rounded-md border border-emerald-700/60 bg-emerald-950/30 p-4 lg:w-3/4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 text-emerald-300">
              <FontAwesomeIcon icon={faCircleCheck} />
              <h2 className="font-semibold">Token created — copy it below</h2>
            </div>
            <button
              type="button"
              onClick={() => setJustMinted(null)}
              className="text-gray-400 hover:text-gray-100"
              aria-label="Dismiss"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
          <p className="mt-2 text-sm text-gray-300">
            {(() => {
              const { kind, role } = decomposeScope(justMinted.scope)
              return (
                <>
                  <strong>{justMinted.name}</strong> · {kindLabel(kind)}
                  {kind === 'rundown' && justMinted.rundownId
                    ? ` (${justMinted.rundownId})`
                    : ''}{' '}
                  · {roleLabel(role)}
                  {justMinted.expiresAt
                    ? ` · expires ${formatDateTime(justMinted.expiresAt)}`
                    : ' · no expiry'}
                </>
              )
            })()}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            You can copy this value again later from the list below.
          </p>
          <div className="mt-3 flex items-stretch gap-2">
            <textarea
              value={
                revealMinted ? justMinted.token : maskToken(justMinted.token)
              }
              readOnly
              rows={3}
              className={cn(
                'flex-1 bg-gray-900 rounded-sm p-3 text-sm font-mono resize-none focus:outline-hidden',
                !revealMinted && 'opacity-60',
              )}
              onClick={(e) => (e.target as HTMLTextAreaElement).select()}
            />
            <div className="flex flex-col gap-2">
              <Button
                text={revealMinted ? 'Hide' : 'Reveal'}
                colour="dark"
                onClick={() => setRevealMinted(!revealMinted)}
              />
              <Button
                icon={faCopy}
                text="Copy"
                colour="main"
                onClick={handleCopyMinted}
              />
            </div>
          </div>
        </div>
      )}

      {/* Active tokens list */}
      <div className="mt-6 lg:w-3/4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">API Tokens</h2>
          <Button
            icon={faPlus}
            text="Generate token"
            colour="main"
            onClick={handleCreateOpen}
            disabled={!isTeamAdmin}
            toolTip={!isTeamAdmin ? 'Only team admins can mint tokens.' : ''}
          />
        </div>

        {showCreate && (
          <MintForm
            form={form}
            setForm={setForm}
            rundownOptions={rundownOptions}
            loading={mintMutation.isPending}
            onCancel={() => {
              setShowCreate(false)
              setForm(buildEmptyForm())
            }}
            onSubmit={handleMint}
          />
        )}

        <div className="rounded-md border border-gray-800 overflow-hidden">
          {tokensLoading && tokens.length === 0 && (
            <div className="p-4 text-sm text-gray-400">Loading tokens…</div>
          )}
          {!tokensLoading && tokens.length === 0 && (
            <div className="p-4 text-sm text-gray-400">
              {isTeamAdmin
                ? 'No tokens yet. Generate one to start calling the API.'
                : 'Only team admins can view and manage API tokens.'}
            </div>
          )}
          {tokens.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-gray-900 text-gray-400 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Scope</th>
                  <th className="text-left p-3">Role</th>
                  <th className="text-left p-3">Created</th>
                  <th className="text-left p-3">Last used</th>
                  <th className="text-left p-3">Expires</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => {
                  const { kind, role } = decomposeScope(t.scope)
                  return (
                    <tr
                      key={t.jti}
                      className="border-t border-gray-800"
                    >
                      <td className="p-3">
                        <div className="font-medium">{t.name}</div>
                      </td>
                      <td className="p-3">
                        <div>{kindLabel(kind)}</div>
                        {kind === 'rundown' && t.rundownId && (
                          <div className="text-xs text-gray-500">
                            {rundownNameById.get(t.rundownId) ?? t.rundownId}
                          </div>
                        )}
                      </td>
                      <td className="p-3">{roleLabel(role)}</td>
                      <td className="p-3 text-gray-300">
                        {formatDate(t.createdAt)}
                      </td>
                      <td className="p-3 text-gray-300">
                        {formatDate(t.lastUsedAt)}
                      </td>
                      <td className="p-3 text-gray-300">
                        {t.expiresAt ? (
                          formatDate(t.expiresAt)
                        ) : (
                          <span className="text-gray-500">Never</span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            icon={faCopy}
                            text="Copy"
                            colour="dark"
                            size="sm"
                            onClick={() => handleCopyToken(t.jti)}
                            loading={copyingJti === t.jti}
                            disabled={!isTeamAdmin}
                            toolTip={
                              !isTeamAdmin
                                ? 'Only team admins can copy tokens.'
                                : 'Copy this token’s value'
                            }
                          />
                          <Button
                            icon={faTrash}
                            text="Revoke"
                            colour="dark"
                            size="sm"
                            onClick={() => handleRevoke(t.jti)}
                            loading={
                              revokingJti === t.jti && revokeMutation.isPending
                            }
                            disabled={!isTeamAdmin}
                          />
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardWrapper>
  )
}

interface MintFormProps {
  form: MintFormState
  setForm: (next: MintFormState) => void
  rundownOptions: Array<{ label: string; value: string }>
  loading: boolean
  onCancel: () => void
  onSubmit: () => void
}

function MintForm({
  form,
  setForm,
  rundownOptions,
  loading,
  onCancel,
  onSubmit,
}: MintFormProps) {
  return (
    <div className="mb-4 rounded-md border border-gray-800 bg-gray-900/40 p-4">
      <h3 className="font-semibold mb-3">New token</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Name"
          value={form.name}
          onChange={(v) => setForm({ ...form, name: v })}
          placeholder="e.g. Companion control"
        />
        <AdvancedSelect
          label="Scope"
          placeholder="Scope"
          options={[
            { label: 'Team — all rundowns', value: 'team' },
            { label: 'Rundown — single rundown', value: 'rundown' },
          ]}
          value={form.kind}
          setValue={(v: ScopeKind) => {
            // OWNER only exists for team scope; downgrade to editor if user
            // switches a rundown selection while having owner role staged.
            const role: RoleChoice =
              v === 'rundown' && form.role === 'owner' ? 'editor' : form.role
            setForm({ ...form, kind: v, role })
          }}
        />
        {form.kind === 'rundown' && (
          <AdvancedSelect
            label="Rundown"
            placeholder="Pick a rundown"
            containerClassName="sm:col-span-2"
            options={rundownOptions}
            value={form.rundownId}
            setValue={(v: string) => setForm({ ...form, rundownId: v })}
          />
        )}
        <AdvancedSelect
          label="Role"
          placeholder="Role"
          options={[
            { label: 'View — read only', value: 'viewer' },
            { label: 'Showcall — read + control', value: 'showcaller' },
            { label: 'Edit — read/write/control', value: 'editor' },
            ...(form.kind === 'team'
              ? [{ label: 'Owner — Edit + delete rundowns', value: 'owner' }]
              : []),
          ]}
          value={form.role}
          setValue={(v: RoleChoice) => setForm({ ...form, role: v })}
        />
        <AdvancedSelect
          label="Expires"
          placeholder="Expires"
          options={[
            { label: '24 hours', value: '24h' },
            { label: '7 days', value: '7d' },
            { label: '30 days', value: '30d' },
            { label: 'Never', value: 'never' },
          ]}
          value={form.expiry}
          setValue={(v: ExpiryChoice) => setForm({ ...form, expiry: v })}
        />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button
          text="Cancel"
          colour="none"
          onClick={onCancel}
        />
        <Button
          text="Generate"
          colour="main"
          onClick={onSubmit}
          loading={loading}
          disabled={!form.name.trim()}
        />
      </div>
    </div>
  )
}

function maskToken(token: string): string {
  if (!token) return ''
  const first = token.slice(0, 4)
  const last = token.slice(-6)
  const middle = '•'.repeat(Math.max(0, token.length - 10))
  return `${first}${middle}${last}`
}
